import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getRepairDoneList, deleteRepair } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { canDelete } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDateTime } from '../../utils/tableExport';

const RepairDone = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRepairDoneList();
      setRows(res.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load repair done list'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (r) => {
    if (!canDelete()) return;
    const label = r.grn_number || r.service_number || `#${r.id}`;
    if (!window.confirm(`Delete closed job ${label}?`)) return;
    try {
      setBusyId(r.id);
      await deleteRepair(r.id);
      setSuccess(`Deleted ${label}`);
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Delete failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Closed Job</Typography>
          <Typography variant="body2" color="text.secondary">
            Jobs collected by the customer (closed).
          </Typography>
        </Box>
        <ExportButtons
          title="Closed Jobs"
          filename="closed_jobs"
          columns={[
            { label: 'JOB #', value: (r) => dash(r.grn_number || r.service_number) },
            { label: 'Customer', value: (r) => dash(r.customer_name) },
            { label: 'Device', value: (r) => [r.device_brand, r.device_model].filter(Boolean).join(' ') || '-' },
            { label: 'Collected', value: (r) => fmtExportDateTime(r.collected_at) },
            { label: 'Collected by', value: (r) => dash(r.collected_by) },
            { label: 'Summary', value: (r) => dash(r.repair_completion_summary) }
          ]}
          rows={rows}
          onError={setError}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>JOB #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Collected</TableCell>
                <TableCell>Collected by</TableCell>
                <TableCell>Summary</TableCell>
                {canDelete() && <TableCell>Delete</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canDelete() ? 7 : 6} align="center">No completed collections yet</TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/repairs/${r.id}`)}>
                  <TableCell>
                    <Chip label={r.grn_number || r.service_number} size="small" color="success" sx={{ fontFamily: 'monospace' }} />
                  </TableCell>
                  <TableCell>{r.customer_name}</TableCell>
                  <TableCell>{r.device_brand} {r.device_model}</TableCell>
                  <TableCell>
                    {r.collected_at ? new Date(r.collected_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>{r.collected_by || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap sx={{ maxWidth: 220, display: 'block' }}>
                      {r.repair_completion_summary || '-'}
                    </Typography>
                  </TableCell>
                  {canDelete() && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" color="error" disabled={busyId === r.id} onClick={() => handleDelete(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RepairDone;
